import { environment } from '../environments/environment';

export type ActiveMsalSettings = {
  clientId: string;
  tenantId: string;
};

function getStoredValue(storageKey: string): string | null {
  const stored = sessionStorage.getItem(storageKey) ?? localStorage.getItem(storageKey);
  if (!stored) {
    return null;
  }

  const trimmed = stored.trim();
  return trimmed.length > 0 ? trimmed : null;
}

// App multi-tenant: un solo registro en Entra al que cada cliente da su consentimiento.
// El usuario se autentica contra su propio tenant mediante la autoridad "organizations",
// así que dar de alta un cliente nuevo no requiere tocar configuración ni redesplegar.
export function resolveClientIdForEmail(_email: string): string {
  const clientId = environment.msalConfig.clientId?.trim();
  if (!clientId) {
    throw new Error('[MSAL] environment.msalConfig.clientId is not configured.');
  }

  return clientId;
}

export function resolveTenantIdForEmail(_email: string): string {
  const tenantId = environment.msalConfig.tenantId?.trim();
  return tenantId && tenantId.length > 0 ? tenantId : 'organizations';
}

export function getActiveMsalClientId(): string {
  const settings = tryGetActiveMsalSettings();
  if (!settings) {
    throw new Error('[MSAL] Active client ID is not set. Call setActiveMsalSettingsForEmail first.');
  }

  return settings.clientId;
}

export function getActiveMsalTenantId(): string {
  const settings = tryGetActiveMsalSettings();
  if (!settings) {
    throw new Error('[MSAL] Active tenant ID is not set. Call setActiveMsalSettingsForEmail first.');
  }

  return settings.tenantId;
}

export function setActiveMsalClientIdForEmail(email: string): string {
  const clientId = resolveClientIdForEmail(email);
  sessionStorage.setItem(environment.msalSelectedClientIdStorageKey, clientId);
  localStorage.setItem(environment.msalSelectedClientIdStorageKey, clientId);
  return clientId;
}

export function setActiveMsalTenantIdForEmail(email: string): string {
  const tenantId = resolveTenantIdForEmail(email);
  sessionStorage.setItem(environment.msalSelectedTenantIdStorageKey, tenantId);
  localStorage.setItem(environment.msalSelectedTenantIdStorageKey, tenantId);
  return tenantId;
}

export function setActiveMsalSettingsForEmail(email: string): { clientId: string; tenantId: string } {
  const clientId = setActiveMsalClientIdForEmail(email);
  const tenantId = setActiveMsalTenantIdForEmail(email);
  return { clientId, tenantId };
}

export function tryGetActiveMsalSettings(): ActiveMsalSettings | null {
  const storedClientId = getStoredValue(environment.msalSelectedClientIdStorageKey);
  const storedTenantId = getStoredValue(environment.msalSelectedTenantIdStorageKey);

  const clientId = storedClientId ?? environment.msalConfig.clientId?.trim() ?? null;
  const tenantId = storedTenantId ?? environment.msalConfig.tenantId?.trim() ?? null;

  if (!clientId || !tenantId) {
    return null;
  }

  return {
    clientId,
    tenantId
  };
}

export function getAuthorityForActiveMsalTenant(): string {
  const tenantId = getActiveMsalTenantId();
  return `https://login.microsoftonline.com/${tenantId}`;
}

export function getMsalScopesForActiveClientId(): string[] {
  const clientId = getActiveMsalClientId();
  const apiScope = environment.msalApiScopeTemplate.replace('{clientId}', clientId);

  // El token que va al backend debe tener como audiencia NUESTRA API (api://{clientId}).
  // Si se mezclan scopes de Microsoft Graph (p. ej. User.Read), MSAL emite un token de Graph,
  // que el backend no puede intercambiar por uno de Foundry (AADSTS50013). Los tokens de Graph
  // se piden aparte en entra-branding.service.ts con sus propios scopes.
  const openIdScopes = environment.msalScopes.filter(
    scope => scope === 'openid' || scope === 'profile' || scope === 'offline_access'
  );

  return [...openIdScopes, apiScope];
}

/**
 * Dirección de retorno para las peticiones silenciosas (acquireTokenSilent).
 *
 * Debe ser una página vacía que no cargue Angular ni MSAL. Si se usa /login, el iframe oculto
 * de MSAL arranca la aplicación completa, el router borra el código de autorización de la URL
 * y la renovación falla con timed_out / block_iframe_reload. Microsoft recomienda fijarla por
 * petición cuando la app también usa redirecciones, como es el caso.
 */
export function getSilentRedirectUri(): string {
  return `${window.location.origin}/blank.html`;
}
